import { useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyIcon from '@mui/icons-material/Key';
import NumbersIcon from '@mui/icons-material/Numbers';
import { GridRenderCellParams, GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { useLookupOptions, type LookupOption } from '@/lib/lookupOptions';

/**
 * The cell editors of a column-to-field mapping grid, shared by the Import Profile editor and the
 * online import dialog (integration_architecture.md, Auto-matching and Online import), so the two
 * map a file the same way.
 */

/** A field a column can be mapped to. */
export interface MappingTargetField {
  name: string;
  label?: string;
  required?: boolean;
  /** A key: a row whose value matches an existing record updates it instead of creating one. */
  isKey?: boolean;
  /** Numbered by a number series: a new record gets its number from the series, never from the file. */
  serverAssigned?: boolean;
  referencedEntity?: string | null;
  referencedModule?: string | null;
}

/** The key mark beside a field: the field a row is matched on, said in words on hover. */
function KeyMark() {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('importMapping.keyHint', {
      defaultValue: 'Key: a row with a value an existing record has updates that record; any other creates one',
    })}>
      <KeyIcon
        aria-label={t('importMapping.key', { defaultValue: 'Key' })}
        sx={{ fontSize: 16, color: 'warning.main', ml: 0.5, verticalAlign: 'middle' }}
      />
    </Tooltip>
  );
}

/**
 * The number-series mark beside a field (the adapter's ServerAssigned): a new record is numbered by
 * the series, so its cell is left empty to create one and a number in it finds the record to update.
 */
function NumberSeriesMark() {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('importMapping.numberSeriesHint', {
      defaultValue: 'Number series: a new record gets its number from the series, so leave the cell empty to create one; a number in the file finds the existing record to update',
    })}>
      <NumbersIcon
        aria-label={t('importMapping.numberSeries', { defaultValue: 'Number series' })}
        data-testid="NumberSeriesMark"
        sx={{ fontSize: 16, color: 'info.main', ml: 0.5, verticalAlign: 'middle' }}
      />
    </Tooltip>
  );
}

/**
 * The required mark beside a field: a new record cannot be created without it (required in the
 * DSL, no default, not server-assigned — the adapter's RequiredOnCreate), said in words on hover.
 */
function RequiredMark() {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('importMapping.requiredHint', {
      defaultValue: 'Required: a new record cannot be created without it; a row that updates an existing record may leave it empty',
    })}>
      <Box
        component="span"
        aria-label={t('importMapping.required', { defaultValue: 'Required' })}
        data-testid="RequiredMark"
        sx={{ color: 'error.main', fontWeight: 600, ml: 0.5 }}
      >
        *
      </Box>
    </Tooltip>
  );
}

/**
 * The target field as the grid shows it: its label, the required mark when a new record needs it,
 * the number-series mark when a series numbers it, the key mark when it is a key.
 */
export function TargetFieldCell({ value, entityFields }: GridRenderCellParams & { entityFields: MappingTargetField[] }) {
  const field = entityFields.find((f) => f.name === value);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      {field?.label ?? value ?? ''}
      {field?.required && <RequiredMark />}
      {field?.serverAssigned && <NumberSeriesMark />}
      {field?.isKey && <KeyMark />}
    </Box>
  );
}

/** Dropdown cell editor for the source column. */
export function SourceColumnEditCell(props: GridRenderEditCellParams & { sourceColumns: string[] }) {
  const { id, field, value, sourceColumns } = props;
  const apiRef = useGridApiContext();

  if (sourceColumns.length === 0) return null;

  return (
    <Select
      value={value || ''}
      onChange={(e) => apiRef.current.setEditCellValue({ id, field, value: e.target.value })}
      size="small"
      fullWidth
      variant="standard"
      sx={{ height: '100%', '& .MuiSelect-select': { py: 0.5 } }}
    >
      <MenuItem value=""><em>— none —</em></MenuItem>
      {sourceColumns.map(col => (
        <MenuItem key={col} value={col}>{col}</MenuItem>
      ))}
    </Select>
  );
}

/** Dropdown cell editor for the target field. */
export function TargetFieldEditCell(props: GridRenderEditCellParams & { entityFields: MappingTargetField[] }) {
  const { id, field, value, entityFields } = props;
  const apiRef = useGridApiContext();

  if (entityFields.length === 0) return null;

  return (
    <Select
      value={value || ''}
      onChange={(e) => apiRef.current.setEditCellValue({ id, field, value: e.target.value })}
      size="small"
      fullWidth
      variant="standard"
      sx={{ height: '100%', '& .MuiSelect-select': { py: 0.5 } }}
    >
      <MenuItem value=""><em>— none —</em></MenuItem>
      {entityFields.map(f => (
        <MenuItem key={f.name} value={f.name}>
          {f.name}
          {f.label && f.label !== f.name && (
            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>({f.label})</Typography>
          )}
          {f.required && <RequiredMark />}
          {f.serverAssigned && <NumberSeriesMark />}
          {f.isKey && <KeyMark />}
        </MenuItem>
      ))}
    </Select>
  );
}

/** Lookup edit cell for default values on reference fields */
export function DefaultLookupEditCell(props: GridRenderEditCellParams & { fieldMeta: MappingTargetField }) {
  const { id, field, value, fieldMeta } = props;
  const apiRef = useGridApiContext();
  const [inputValue, setInputValue] = useState('');
  const entity = fieldMeta.referencedEntity!;
  const module = fieldMeta.referencedModule!;

  // Searches from the first typed character; an empty input shows no list.
  const { options: found, loading } = useLookupOptions({
    module, entity, query: inputValue, pageSize: 10, enabled: inputValue.length >= 1,
  });
  const options = inputValue.length >= 1 ? found : [];

  // Find display text for current value (if it's a UUID)
  const displayLabel = (opt: LookupOption | string): string => {
    if (!opt || typeof opt === 'string') return opt || '';
    // Use the first non-id string property as label
    const record = opt as unknown as Record<string, unknown>;
    return Object.keys(record).filter(k => k !== 'id').map(k => record[k]).filter(Boolean).join(' — ');
  };

  return (
    <Autocomplete<LookupOption, false, false, true>
      freeSolo
      size="small"
      fullWidth
      options={options}
      loading={loading}
      value={value || ''}
      inputValue={inputValue}
      onInputChange={(_e, v) => setInputValue(v)}
      onChange={(_e, newVal) => {
        const newId = typeof newVal === 'string' ? newVal : newVal?.id || '';
        apiRef.current.setEditCellValue({ id, field, value: newId });
      }}
      getOptionLabel={displayLabel}
      renderOption={(optionProps, option) => (
        <li {...optionProps} key={option.id}>
          <Typography variant="body2">{displayLabel(option)}</Typography>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder="Search..."
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={14} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      sx={{ height: '100%' }}
    />
  );
}

/** The Transform column's header, with the list of transforms as its tooltip. */
export function TransformColumnHeader({ label = 'Transform' }: { label?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>{label}</Typography>
      <Tooltip
        title={
          <Box sx={{ fontSize: '0.75rem', lineHeight: 1.6 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Pipe-separated transforms (left-to-right):
            </Typography>
            <code>trim</code> — Remove whitespace<br />
            <code>upper</code> / <code>lower</code> — Change case<br />
            <code>left:N</code> / <code>right:N</code> — First/last N chars<br />
            <code>substring:S,L</code> — Extract from position S<br />
            <code>truncate:N</code> — Limit to N chars<br />
            <code>replace:old,new</code> — Replace text<br />
            <code>coalesce:fallback</code> — Default if empty<br />
            <code>parsedecimal</code> — Normalize numbers<br />
            <code>parsedate:format</code> — Parse date (e.g. dd/MM/yyyy)<br />
            <code>parsebool</code> — yes/no/1/0 → true/false<br />
            <code>mapenum:A=X,B=Y</code> — Map values<br />
            <code>clear</code> — Empty cell empties the field on update (last in the chain)<br />
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 0.5 }}>
              Examples:
            </Typography>
            <code>trim|upper</code><br />
            <code>trim|parsedecimal</code><br />
            <code>trim|parsedate:dd/MM/yyyy</code><br />
            <code>trim|replace:-,|parsedecimal</code>
          </Box>
        }
        arrow
        placement="top"
      >
        <HelpOutlineIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
}
